import { useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Elapsed } from "./RunTimeline";

/* A turn's header, affect-ui's Thinking Loader. While the run works: eight rays fading round,
   the label rolling to what the run is doing ("Searching the web" → "Writing response"), and a
   timer. Once done the rays turn into a check and the label settles on "Worked for 22s". A
   click folds the turn's work under it, or opens it again. */

const RAYS = [0, 1, 2, 3, 4, 5, 6, 7];
const RAY_STEP_MS = 100;
// offsetWidth rounds down; a pixel more keeps the label's last letter from being cut
const ROUNDING_PX = 1;

export default function TurnHeader({ label, live, since, open, onToggle }: {
  label: string;
  live: boolean;
  /** When the run started (epoch ms): the timer's start while it works. */
  since?: number;
  /** null: nothing to fold. */
  open: boolean | null;
  onToggle: () => void;
}) {
  // the check draws itself only when this turn finished on screen, not on every saved one
  const watched = useRef(live);
  if (live) watched.current = true;
  const swap = "absolute inset-0 transition-[opacity,transform] [transition-duration:420ms] [transition-timing-function:var(--easing)]";
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={open === null}
      aria-expanded={open ?? undefined}
      className="press group -ml-1 flex items-center gap-2 pl-1 pr-2 py-1 rounded-[8px] enabled:hover:bg-[var(--surface-2)] text-left min-w-0 max-w-full"
    >
      <span aria-hidden className="relative w-[15px] h-[15px] shrink-0 text-[var(--text-primary)]">
        <svg viewBox="0 0 16 16" className={`${swap} overflow-visible ${live ? "" : "opacity-0 scale-[0.6] -rotate-45"}`}>
          {RAYS.map((i) => (
            <line
              key={i}
              x1="8" y1="1.75" x2="8" y2="4.75"
              transform={`rotate(${i * 45} 8 8)`}
              className="ray"
              style={{ animationDelay: `${(i - RAYS.length) * RAY_STEP_MS}ms` }}
              stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"
            />
          ))}
        </svg>
        <svg viewBox="0 0 16 16" className={`${swap} ${live ? "opacity-0 scale-[0.6]" : ""}`} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
          {!live && <path d="M3.5 8.4 6.6 11.3 12.5 4.9" pathLength={1} className={watched.current ? "check-draw" : undefined} />}
        </svg>
      </span>
      <RollingLabel text={label} shimmer={live} />
      {live && <span className="shrink-0 text-[13.5px] text-[var(--text-quaternary)]"><Elapsed since={since} /></span>}
      {open !== null && (
        <ChevronDown
          aria-hidden
          strokeWidth={1.75}
          className={`w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)] transition-transform [transition-duration:260ms] [transition-timing-function:var(--easing)] ${open ? "" : "-rotate-90"}`}
        />
      )}
      <span className="sr-only" role="status" aria-live="polite">{label}</span>
    </button>
  );
}

/** The label, the old one rolling up and out as the new one rolls in, the box gliding to the
    new one's width. */
function RollingLabel({ text, shimmer }: { text: string; shimmer: boolean }) {
  const [shown, setShown] = useState({ key: 0, text });
  const [leaving, setLeaving] = useState<{ key: number; text: string } | null>(null);
  if (text !== shown.text) {
    setLeaving(shown);
    setShown({ key: shown.key + 1, text });
  }
  const box = useRef<HTMLSpanElement>(null);
  const current = useRef<HTMLSpanElement>(null);
  const measured = useRef(false);
  useLayoutEffect(() => {
    if (!box.current || !current.current) return;
    // the first width is set, not glided to
    box.current.style.transition = measured.current ? "" : "none";
    box.current.style.width = `${current.current.offsetWidth + ROUNDING_PX}px`;
    measured.current = true;
  }, [shown.key]);
  const tone = shimmer ? "shimmer-text" : "text-[var(--text-tertiary)] group-hover:text-[var(--text-secondary)]";
  return (
    <span ref={box} aria-hidden className="relative block h-[1.3em] min-w-0 overflow-hidden text-[13.5px] leading-[1.3em] transition-[width] [transition-duration:460ms] [transition-timing-function:var(--easing)]">
      {/* the roll and the shimmer are each an animation, so each has its own span */}
      {leaving && (
        <span key={leaving.key} className="absolute left-0 top-0 whitespace-nowrap roll-out" onAnimationEnd={() => setLeaving(null)}>
          <span className={tone}>{leaving.text}</span>
        </span>
      )}
      <span key={shown.key} ref={current} className={`absolute left-0 top-0 whitespace-nowrap ${shown.key > 0 ? "roll-in" : ""}`}>
        <span className={tone}>{shown.text}</span>
      </span>
    </span>
  );
}
